import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  Stack,
  TableRow,
  Card,
  FormControl,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { ReactComponent as GrowthSymbol } from '../../assests/images/Symbol.svg'; // Make sure the path is correct
import {
  Error,
  Flag,
  Lightbulb,
  ReportOff,
  ThumbDown,
  ThumbDownOffAlt,
  ThumbUp,
  ThumbUpOffAlt
} from '@mui/icons-material';
import LineChart from '../Charts/LineChart/LineChart';
import CustomTabs from '../CustomTabs/CustomTabs';
import BreakdownTable from '../Table/BreakdownTable';
import {
  fetchCYLYData,
  fetchProductChannelBreakdown,
  fetchProductGrowth,
  fetchProductKPI,
  fetchProductRegionBreakdown
} from '../../pages/FeedbackCollation/servies';

const closeIconStyle = {
  color: 'black',
  fontSize: '22px',
  cursor: 'pointer'
};

const SKUModal = (props) => {
  const tabs = [
    {
      id: 'region',
      name: 'Region',
      showCycle: false,
      showTimeframe: true,
      showBu: true
    },
    {
      id: 'channel',
      name: 'Channel',
      showCycle: false,
      showTimeframe: true,
      showBu: true
    }
  ];

  const {
    open,
    onClose,
    rowData,
    rowIndex,
    wholeData,
    productAttr,
    setRowIndex,
    setSKUData,
    onDrawerCommentChange,
    currentVotingTeam,
    setRowData
  } = props;
  const [rindex, setRIndex] = useState(0);
  const [data, setData] = useState({});
  const [lineChartData, setLineChartData] = useState({});
  const [productKPIData, setProductKPIData] = useState([]);
  const [productGrowthData, setProductGrowthData] = useState([]);
  const [productChannelBreakDownData, setProductChannelBreakDownData] = useState([]);
  const [productRegionBreakDownData, setProducRegionBreakDownData] = useState([]);
  const [selectedTab, setSelectedTab] = useState(tabs[0].id);
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedBu = newParams.get('bu');

  const [isLineChartLoading, setIsLineChartLoading] = useState(false);
  const [loadersKPI, setLoadersKPI] = useState(false);
  const [loadersGrowth, setLoadersGrowth] = useState(false);
  const [tableProductAttr, setTableProductAttr] = useState({});
  const [thumbsUpRegion, setThumbsUpRegion] = useState(0);
  const [thumbsDownRegion, setThumbsDownRegion] = useState(0);
  const [thumbsUpFunctional, setThumbsUpFunctional] = useState(0);
  const [thumbsDownFunctional, setThumbsDownFunctional] = useState(0);

  const wholeDataMemo = useMemo(() => wholeData, [wholeData]); //memoized whole data

  useEffect(() => {
    setData(rowData);
    setRIndex(rowIndex);
  }, [rowData]);

  useEffect(() => {
    if (!productAttr?.tpoSkuKey || !selectedBu) return;

    const keysToRemove = ['tpoSkuKey', 'tpoSku', 'upc', 'materialIdCount', 'tpoSkuName'];
    const filteredContent = Object.fromEntries(
      Object.entries(productAttr).filter(([key]) => !keysToRemove.includes(key))
    );
    setTableProductAttr(filteredContent);

    if (productAttr?.tpoSkuKey && selectedBu && selectedCycleId) {
      getCYLYData(productAttr?.tpoSkuKey, selectedBu, selectedCycleId);
      getProductKPIData(productAttr?.tpoSkuKey, selectedBu, selectedCycleId);
      getProductGrowthData(productAttr?.tpoSkuKey, selectedBu, selectedCycleId);
    }

    if (rowData) {
      let upCount = 0;
      let downCount = 0;

      // Extract votes from regional team votes array
      if (Array.isArray(rowData?.['regional team votes'])) {
        rowData['regional team votes'].forEach((item) => {
          const key = Object.keys(item)[0]; // Get the key
          if (key.includes('agree steering team reco')) {
            const value = item[key];
            if (value === true) upCount++;
            else if (value === false) downCount++;
          }
        });
      }

      setThumbsUpRegion(upCount);
      setThumbsDownRegion(downCount);

      if (rowData?.['functional team votes']) {
        setThumbsUpFunctional(rowData?.['functional team votes']?.functionalteamupvotes);
        setThumbsDownFunctional(rowData?.['functional team votes']?.functionalteamdownvotes);
      } else {
        setThumbsUpFunctional(0);
        setThumbsDownFunctional(0);
      }
    }
  }, [productAttr?.tpoSkuKey]);

  useEffect(() => {
    if (productAttr?.tpoSkuKey && selectedBu && selectedCycleId) {
      if (selectedTab === 'region') {
        getProductRegionBreakdownData(productAttr?.tpoSkuKey, selectedBu, selectedCycleId);
      } else {
        getProductChannelBreakdownData(productAttr?.tpoSkuKey, selectedBu, selectedCycleId);
      }
    }
  }, [productAttr?.tpoSkuKey, selectedTab]);

  const getCYLYData = async (tpoSKUKey, selectedBu, selectedCycleId) => {
    try {
      setIsLineChartLoading(true);

      const content = await fetchCYLYData(tpoSKUKey, selectedBu, selectedCycleId);
      const previousList = await convertToDollarLabel(content?.datasets?.[0]?.data);
      const currentList = await convertToDollarLabel(content?.datasets?.[1]?.data);

      setLineChartData({
        labels: content?.labels,
        datasets: [
          { label: 'Last Year', data: previousList },
          { label: 'Current Year', data: currentList }
        ]
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLineChartLoading(false);
    }
  };

  const getProductKPIData = async (tpoSKUKey, selectedBu, selectedCycleId) => {
    try {
      setLoadersKPI(true);
      const content = await fetchProductKPI(tpoSKUKey, selectedBu, selectedCycleId);
      setProductKPIData(content);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoadersKPI(false);
    }
  };

  const getProductChannelBreakdownData = async (tpoSKUKey, selectedBu, selectedCycleId) => {
    try {
      const content = await fetchProductChannelBreakdown(tpoSKUKey, selectedBu, selectedCycleId);
      setProductChannelBreakDownData(content);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getProductRegionBreakdownData = async (tpoSKUKey, selectedBu, selectedCycleId) => {
    try {
      const content = await fetchProductRegionBreakdown(tpoSKUKey, selectedBu, selectedCycleId);
      setProducRegionBreakDownData(content);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getProductGrowthData = async (tpoSKUKey, selectedBu, selectedCycleId) => {
    try {
      setLoadersGrowth(true);
      const content = await fetchProductGrowth(tpoSKUKey, selectedBu, selectedCycleId);
      setProductGrowthData(content);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoadersGrowth(false);
    }
  };

  const convertToDollarLabel = (dataList) => {
    if (!Array.isArray(dataList)) {
      let num = parseFloat(dataList?.toString().replace(/,/g, ''));
      return `${num.toFixed(2)}`;
    }
    return dataList.map(convertToDollarLabel);
  };

  const handlePrev = () => {
    if (rindex > 0) {
      const newIndex = rindex - 1;
      setRIndex(newIndex);
      setRowIndex(newIndex);
      setSKUData(wholeDataMemo[newIndex].tposku);
      setData(wholeDataMemo[newIndex]);
      setRowData(wholeDataMemo[newIndex]);
    }
  };
  const handleNext = () => {
    if (rindex < wholeDataMemo.length - 1) {
      const newIndex = rindex + 1;
      setRIndex(newIndex);
      setRowIndex(newIndex);
      setSKUData(wholeDataMemo[newIndex].tposku);
      setData(wholeDataMemo[newIndex]);
      setRowData(wholeDataMemo[newIndex]);
    }
  };
  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: '10000' }}>
      {/* main Box */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          width: '95%',
          maxHeight: '90vh', // Set max height for scrolling
          left: '50px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: '#FBFCFE',
          border: '10px solid #D7E5F8',
          boxShadow: '0px 2px 24px 8px rgba(0, 92, 188, 0.12)',
          overflowY: 'auto', // Enable vertical scrolling
          scrollbarWidth: 'thin', // For Firefox
          scrollbarColor: 'rgba(0,0,0,0.2) transparent', // For Firefox
          '&::WebkitScrollbar': {
            width: '6px'
          },
          '&::WebkitScrollbarTrack': {
            background: 'transparent'
          },
          '&::WebkitScrollbarThumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '3px'
          }
        }}>
        {/* Close Button */}
        <Box sx={{ textAlign: 'end' }}>
          <CloseIcon
            data-testid="SKUclose-icon"
            role="button"
            onClick={onClose}
            sx={closeIconStyle}
          />
        </Box>
        {/* header box */}
        <Box>
          <Typography variant="h6" sx={{ alignSelf: 'flex-start', marginLeft: '20px' }}>
            {productAttr?.tpoSkuName}
          </Typography>
        </Box>
        {/* Growth Box */}
        <Box style={{ display: 'flex', direction: 'row' }}>
          <Card
            sx={{
              height: '105px',
              background: '#EFF5FC',
              display: 'flex',
              alignItems: 'center',
              margin: '10px 20px',
              width: '475px',
              minHeight: '10vh',
              // padding: "10px",
              // maxWidth:'95%',
              justifyContent: 'space-between'
            }}>
            {/* Left Section */}
            <Stack direction="row" alignItems="center">
              {data?.['quadrant'] === 'Good Performers' && (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    backgroundColor: 'green',
                    borderRadius: '10px',
                    padding: '5px 10px',
                    marginRight: '5px',
                    marginLeft: '5px'
                  }}>
                  <ThumbUp style={{ marginLeft: '5px', fontSize: 35, color: 'white' }} />
                </Box>
              )}{' '}
              {data?.['quadrant'] === 'Growth Opportunities' && (
                <GrowthSymbol style={{ marginLeft: '5px', width: '40px', height: '40px' }} />
              )}
              {data?.['quadrant'] === 'Poor Performers' && (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    backgroundColor: 'red',
                    borderRadius: '10px',
                    padding: '5px 10px',
                    marginRight: '5px',
                    marginLeft: '5px'
                  }}>
                  <ThumbDown style={{ marginLeft: '5px', fontSize: 35, color: 'white' }} />
                </Box>
              )}{' '}
              {data?.['quadrant'] === 'Exceptions' && (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    backgroundColor: 'inherit',
                    borderRadius: '10px',
                    padding: '5px 10px',
                    marginRight: '5px',
                    marginLeft: '5px'
                  }}>
                  <Error style={{ marginLeft: '5px', fontSize: 35 }} />
                </Box>
              )}{' '}
              {data?.['quadrant'] === 'Profit Opportunities' && (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    backgroundColor: '#ff891c',
                    borderRadius: '10px',
                    padding: '5px 10px',
                    marginRight: '5px',
                    marginLeft: '5px'
                  }}>
                  <Flag style={{ marginLeft: '5px', fontSize: 35, color: 'white' }} />
                </Box>
              )}{' '}
              {data?.['quadrant'] === 'New Innovation' && (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    backgroundColor: 'inherit',
                    borderRadius: '10px',
                    padding: '5px 10px',
                    marginRight: '5px',
                    marginLeft: '5px'
                  }}>
                  <Lightbulb style={{ marginLeft: '5px', fontSize: 35 }} />
                </Box>
              )}{' '}
              {data?.['quadrant'] === 'Discontinued' && (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    backgroundColor: 'inherit',
                    borderRadius: '10px',
                    padding: '5px 10px',
                    marginRight: '5px',
                    marginLeft: '5px'
                  }}>
                  <ReportOff style={{ marginLeft: '5px', fontSize: 35 }} />
                </Box>
              )}
              <Stack>
                <Typography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                  {data?.['quadrant']}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: '400', fontSize: '14px' }}>
                  {data?.['national classification']}
                </Typography>
              </Stack>
            </Stack>
          </Card>
          {/* product growth */}
          <FormControl variant="outlined" sx={{ margin: '10px 0px 10px 10px' }}>
            <Card
              style={{
                height: '105px',
                maxWidth: '100%',
                width: '1060px', // Increased width
                background: '#FBFCFE',
                display: 'flex',
                alignItems: 'center',
                minWidth: '80%',
                padding: '10px',
                border: '1px solid #D7E5F8', // Added border
                borderRadius: '8px' // Optional: rounded corners
                // marginTop: '10px'
              }}>
              {loadersKPI && <CircularProgress />}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '1060px' }}>
                {(productKPIData || []).map((item, index) => (
                  <React.Fragment key={`${index + 1}`}>
                    <FormControl variant="outlined" sx={{ width: 'auto' }}>
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: 'nowrap', fontWeight: '600', fontSize: '12px' }}>
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'normal', fontWeight: '600', fontSize: '18px' }}>
                        {item.value || '--'}
                      </Typography>
                      {item.title === 'ACTION RECO' ? (
                        <></>
                      ) : (
                        <Typography
                          variant="body3"
                          sx={{
                            whiteSpace: 'nowrap',
                            fontWeight: '400',
                            fontSize: '9px',
                            marginTop: '5px',
                            color: item.textcolor || 'inherit'
                          }}>
                          {item.percentdifference
                            ? `${item.percentdifference} vs Previous 13 Periods`
                            : '-- vs Previous 13 Periods'}
                        </Typography>
                      )}
                    </FormControl>
                    {index < productKPIData.length - 1 && (
                      <Divider orientation="vertical" flexItem />
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            </Card>
          </FormControl>
        </Box>

        {/* lower section Box*/}
        <Box style={{ display: 'flex', direction: 'row' }}>
          {/* Attribute section */}
          <Card
            style={{
              height: 'auto',
              background: '#EFF5FC',
              display: 'flex',
              margin: '10px 10px 10px 20px',
              width: '475px'
            }}>
            <FormControl variant="outlined" sx={{ margin: '10px' }}>
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'nowrap', fontWeight: '700', fontSize: '18px' }}>
                Product Attributes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  fontWeight: '700',
                  fontSize: '14px',
                  marginTop: '10px'
                }}>
                UPC GTIN: {productAttr?.['upc'] ?? '--'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  fontWeight: '700',
                  fontSize: '14px',
                  marginTop: '10px'
                }}>
                Material ID Count: {productAttr?.['materialIdCount'] ?? '--'}
              </Typography>
              <Table
                sx={{ width: '100%', marginTop: '20px' }}
                aria-label="product attributes table"
                size="small">
                <TableBody>
                  {Object.keys(tableProductAttr).map((key, index) => (
                    <TableRow
                      key={`${index + 1}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          padding: '5px',
                          fontWeight: '600',
                          paddingRight: '20px',
                          width: '40%'
                        }} // Added paddingRight
                      >
                        {key.toUpperCase()}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          textAlign: 'left',
                          padding: '5px',
                          paddingLeft: '20px',
                          width: '60%'
                        }} // Added paddingLeft
                      >
                        {productAttr[key] || '--'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </FormControl>
          </Card>
          <FormControl variant="outlined" sx={{ margin: '10px 0px 10px 10px' }}>
            {/* avg distribution */}
            <Card
              style={{
                height: '80px',
                maxWidth: '100%',
                width: '1060px',
                background: '#FBFCFE',
                display: 'flex',
                alignItems: 'center',
                marginTop: '5px',
                marginLeft: '10px',
                padding: '10px',
                border: '1px solid #D7E5F8',
                borderRadius: '8px',
                minWidth: '80%'
              }}>
              {loadersGrowth && <CircularProgress />}

              <Stack direction="row" spacing={4} alignItems="center" sx={{ width: '1060px' }}>
                {(productGrowthData || [])?.map((item, index) => (
                  <React.Fragment key={`${index + 1}`}>
                    {/* Dynamic FormControl for each key-value pair */}
                    <FormControl variant="outlined" sx={{ width: 'auto' }}>
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: 'nowrap', fontWeight: '700', fontSize: '18px' }}>
                        {item.value || '--'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'nowrap', fontWeight: '400', fontSize: '14px' }}>
                        {item.title}
                      </Typography>
                    </FormControl>

                    {/* Add Divider between items except the last one */}
                    {index < productGrowthData.length - 1 && (
                      <Divider orientation="vertical" flexItem />
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            </Card>

            {/* Graph + Reco count box */}
            <Box sx={{ display: 'flex', direction: 'row' }}>
              {/* Graph box */}
              <Card
                style={{
                  height: '355px',
                  width: '500px',
                  background: '#FBFCFE',
                  marginTop: '10px',
                  marginLeft: '10px',
                  border: '1px solid #D7E5F8' // Added border
                }}>
                <LineChart
                  title={`CY VS LY - REVENUE`}
                  chartData={lineChartData}
                  valueLabel={'Revenue'}
                  isLineChartLoading={isLineChartLoading}
                  page={'Voting'}
                />
              </Card>
              {/* Reco Count */}
              <Box>
                <Card
                  style={{
                    height: '76px',
                    width: '520px',
                    background: '#FBFCFE',
                    marginTop: '10px',
                    marginLeft: '20px',
                    padding: '10px'
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', marginTop: '10px' }}>
                    <Stack>
                      <Typography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                        SteeringCo Reco:
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: '500', fontSize: '17px', nowrap: 'normal' }}>
                      {currentVotingTeam === 'Steering Reco'
                        ? data?.['votingVotesEntities']?.[0]?.Recommendation
                          ? data?.['votingVotesEntities']?.[0]?.Recommendation
                          : '--'
                        : data?.['steering team recommendation']
                          ? data?.['steering team recommendation']
                          : '--'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Box
                      style={{ fontWeight: 400, color: '#005CBC', cursor: 'pointer' }}
                      onClick={(e) => onDrawerCommentChange(true, 'SteeringReco')}>
                      {`View Comments`}
                    </Box>
                  </Stack>
                </Card>

                <Card
                  style={{
                    height: '76px',
                    width: '520px',
                    background: '#FBFCFE',
                    marginTop: '8px',
                    marginLeft: '20px',
                    padding: '10px'
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', marginTop: '10px' }}>
                    <Stack>
                      <Typography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                        Functional Feedback
                      </Typography>
                    </Stack>
                    <Box
                      style={{
                        textAlign: 'center',
                        display: 'inline-flex',
                        justifyContent: 'space-around',
                        width: '140px'
                      }}>
                      <Box
                        style={{
                          alignItems: 'center',
                          display: 'flex',
                          backgroundColor: '#B3DACB',
                          borderRadius: '10px',
                          padding: '0px 10px'
                        }}>
                        <ThumbUpOffAlt style={{ width: '1em', height: '1em', color: 'black' }} />
                        <span style={{ fontSize: '20px', margin: '5px 2px' }}>
                          {thumbsUpFunctional}
                        </span>
                      </Box>
                      <Box
                        style={{
                          alignItems: 'center',
                          display: 'flex',
                          backgroundColor: '#F6CCD2',
                          borderRadius: '10px',
                          padding: '0px 10px'
                        }}>
                        <ThumbDownOffAlt style={{ width: '1em', height: '1em', color: 'black' }} />
                        <span style={{ fontSize: '20px', margin: '5px 2px' }}>
                          {thumbsDownFunctional}
                        </span>
                      </Box>
                    </Box>
                  </Stack>
                  <Stack>
                    <Box
                      style={{ fontWeight: 400, color: '#005CBC', cursor: 'pointer' }}
                      onClick={(e) => onDrawerCommentChange(true, 'Functional')}>
                      {`View Comments`}
                    </Box>
                  </Stack>
                </Card>

                <Card
                  style={{
                    height: '76px',
                    width: '520px',
                    background: '#FBFCFE',
                    marginTop: '8px',
                    marginLeft: '20px',
                    padding: '10px'
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', marginTop: '10px' }}>
                    <Stack>
                      <Typography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                        Regional Decisions
                      </Typography>
                    </Stack>
                    <Box
                      style={{
                        textAlign: 'center',
                        display: 'inline-flex',
                        justifyContent: 'space-around',
                        width: '140px'
                      }}>
                      <Box
                        style={{
                          alignItems: 'center',
                          display: 'flex',
                          backgroundColor: '#B3DACB',
                          borderRadius: '10px',
                          padding: '0px 10px'
                        }}>
                        <ThumbUpOffAlt style={{ width: '1em', height: '1em', color: 'black' }} />
                        <span style={{ fontSize: '20px', margin: '5px 2px' }}>
                          {thumbsUpRegion}
                        </span>
                      </Box>
                      <Box
                        style={{
                          alignItems: 'center',
                          display: 'flex',
                          backgroundColor: '#F6CCD2',
                          borderRadius: '10px',
                          padding: '0px 10px'
                        }}>
                        <ThumbDownOffAlt style={{ width: '1em', height: '1em', color: 'black' }} />
                        <span style={{ fontSize: '20px', margin: '5px 2px' }}>
                          {thumbsDownRegion}
                        </span>
                      </Box>
                    </Box>
                  </Stack>
                  <Stack>
                    <Box
                      style={{ fontWeight: 400, color: '#005CBC', cursor: 'pointer' }}
                      onClick={(e) => onDrawerCommentChange(true, 'Regional')}>
                      {`View Comments`}
                    </Box>
                  </Stack>
                </Card>

                <Card
                  style={{
                    height: '76px',
                    width: '520px',
                    background: '#FBFCFE',
                    marginTop: '8px',
                    marginLeft: '20px',
                    padding: '10px'
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', marginTop: '10px' }}>
                    <Stack>
                      <Typography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                        Final Decision:
                      </Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                      {data?.['decision'] || '--'}
                    </Typography>
                  </Stack>
                </Card>
              </Box>
            </Box>
            {/* Region Channel table */}
            <Box sx={{ marginTop: '10px' }}>
              <Stack
                direction="row"
                sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'nowrap',
                    fontWeight: '700',
                    fontSize: '18px',
                    margin: '10px 0 0 10px'
                  }}>
                  {selectedTab === 'region' ? 'Region Breakdown' : 'Channel Breakdown'}
                </Typography>
                <CustomTabs
                  tabs={tabs}
                  selectedTab={selectedTab}
                  onTabChange={setSelectedTab}
                  // disabledTabs={}
                  sx={{ marginLeft: '100px' }}
                />
              </Stack>
              {selectedTab && (
                <BreakdownTable
                  // data={regionData}
                  data={
                    selectedTab === 'region'
                      ? productRegionBreakDownData
                      : productChannelBreakDownData
                  }
                />
              )}
            </Box>
          </FormControl>
        </Box>
        {/* Button box */}
        <Box
          p={2}
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #ddd',
            zIndex: 10 // Ensure it stays on top of scrollable content
          }}
          display="flex"
          justifyContent="flex-end"
          alignItems="center">
          <Button
            startIcon={<ArrowBackOutlinedIcon />}
            disabled={rindex === 0}
            variant="outlined"
            onClick={handlePrev}
            sx={{ borderRadius: 20, marginRight: '8px' }}>
            Prior SKU
          </Button>
          <Button
            endIcon={<ArrowForwardOutlinedIcon />}
            disabled={rindex === wholeDataMemo.length - 1}
            variant="outlined"
            onClick={handleNext}
            sx={{ borderRadius: 20 }}>
            Next SKU
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SKUModal;
